/**
 * Who is signed in. A tiny external store (no library): read it in components with `useSession()`,
 * change it with `checkSession`, `signIn` and `signOut`.
 *
 * Any 401 from the API marks the session as signed out; the App then shows the sign-in page and
 * comes back to where the user was afterwards.
 */
import { useSyncExternalStore } from 'react';
import * as api from '../api';

export type SessionState =
  | { status: 'checking' }
  | { status: 'signedOut'; /** Why, when it wasn't the user's choice ("Please sign in again."). */ reason?: string }
  | { status: 'signedIn'; name: string };

const NAME_KEY = 'zp.name';

let state: SessionState = { status: 'checking' };
const listeners = new Set<() => void>();

function set(next: SessionState): void {
  state = next;
  for (const l of listeners) l();
}

/** The current session state (outside React). */
export function getSession(): SessionState {
  return state;
}

/** Subscribes to session changes; returns the unsubscribe function. */
export function subscribeSession(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** React hook: the current session state. */
export function useSession(): SessionState {
  return useSyncExternalStore(subscribeSession, getSession, getSession);
}

/** The signed-in person's name, or '' when nobody is signed in. */
export function currentName(): string {
  return state.status === 'signedIn' ? state.name : '';
}

/** The name this browser used last time (to skip "What's your name?" on a later sign-in). */
export function rememberedName(): string {
  try {
    return localStorage.getItem(NAME_KEY) ?? '';
  } catch {
    return '';
  }
}

/** Forgets the remembered name ("Not Sam?"). */
export function forgetName(): void {
  try {
    localStorage.removeItem(NAME_KEY);
  } catch {
    // Storage blocked: nothing to forget.
  }
}

function rememberName(name: string): void {
  try {
    localStorage.setItem(NAME_KEY, name);
  } catch {
    // Storage blocked: the name prompt just shows again next time.
  }
}

/**
 * Asks the server who is signed in. Offline counts as "still signed in as before" only if we knew;
 * otherwise it rethrows so the App can show "Can't reach the planner".
 */
export async function checkSession(): Promise<void> {
  try {
    const { name } = await api.me();
    rememberName(name);
    set({ status: 'signedIn', name });
  } catch (e) {
    if (e instanceof api.ApiFailure && e.code === 'unauthorized') set({ status: 'signedOut' });
    else throw e;
  }
}

/** Signs in; throws an ApiFailure with a friendly message (wrong password, too many tries…). */
export async function signIn(password: string, name: string): Promise<void> {
  const clean = name.trim();
  await api.login({ password, name: clean });
  rememberName(clean);
  set({ status: 'signedIn', name: clean });
}

/** Signs out on the server (best effort) and locally. */
export async function signOut(): Promise<void> {
  try {
    await api.logout();
  } finally {
    set({ status: 'signedOut' });
  }
}

api.setUnauthorizedHandler(() => {
  if (state.status !== 'signedOut') set({ status: 'signedOut', reason: 'Please sign in again.' });
});
